import React, { useState, useContext, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../contexts/AuthContext";
import GooglePlacesAutocomplete from "../../components/GooglePlacesAutocomplete";
import { sendSignupOTP, verifySignupOTPAndRegister } from "../../services/sellerService";
import { trackSignup, trackSignupStep } from "../../utils/analytics";

const steps = ["Personal", "Shop", "Payment", "Verify"];
const categories = ["Men", "Women", "Kids"]; // enum backend schema

// Shared Tailwind class strings (white + orange glassmorphism theme)
const inputClasses =
  "block w-full px-4 py-3.5 bg-white/70 border-2 border-gray-200 rounded-xl shadow-sm " +
  "focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent " +
  "transition-all duration-200 backdrop-blur-sm text-gray-800 placeholder-gray-400";
const labelClasses = "block text-sm font-semibold text-gray-700 mb-2";
const hintClasses = "text-xs text-gray-400 mt-1";
const primaryBtnClasses =
  "w-full flex items-center justify-center bg-gradient-to-r from-orange-500 to-orange-600 " +
  "hover:from-orange-600 hover:to-orange-700 text-white py-3.5 px-6 rounded-xl font-semibold " +
  "shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed";
const secondaryBtnClasses =
  "w-full border-2 border-gray-200 text-gray-700 bg-white hover:bg-gray-50 py-3 rounded-xl " +
  "font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed";

/**
 * SELLER REGISTRATION DATA COLLECTION
 *
 * This form collects the following information during seller registration:
 *
 * STEP 1 - Personal Details:
 * - firstName (required)
 * - email (required, unique)
 * - password (required, min 6 chars)
 * - mobile (required, 10 digits, unique)
 *
 * STEP 2 - Shop Details:
 * - shopName (required)
 * - shopPhone (required, 10 digits) - NEW: Added for customer contact
 * - address (required, with Google Places autocomplete)
 * - coordinates (auto-extracted from Google Places)
 * - gst (optional)
 * - category (required: Men/Women/Kids)
 *
 * STEP 3 - Payment Details:
 * - upi (required, stored as bankDetails.accountNumber)
 *
 * Fields NOT collected during registration (managed later via profile updates):
 * - Shop operating hours, working days, description
 * - Shop images and main image
 * - Complete bank details (IFSC, bank name, account type)
 * - Location details (city, state, postal code - defaults to Mumbai)
 * - Alternate phone number
 */

const SellerRegister = () => {
  const navigate = useNavigate();

  // 🎯 ADD: Get loginSeller from AuthContext
  const { loginSeller: contextLoginSeller } = useContext(AuthContext);

  const [step, setStep] = useState(0); // 0-2: Form steps, 3: Verify OTP
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    password: "",
    mobile: "",
    address: "",
    shopName: "",
    shopPhone: "",
    gst: "",
    category: "",
    upi: "",
  });

  // 🎯 NEW: Add state for coordinates
  const [shopCoordinates, setShopCoordinates] = useState(null);

  // 🔧 NEW: OTP-related state
  const [otp, setOtp] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const otpRefs = useRef([]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const next = () => {
    if (step < 2) {
      // Normal form navigation
      setStep((s) => Math.min(s + 1, 2));
    } else if (step === 2) {
      // After final form step, move to OTP sending
      handleSendOTP();
    }
  };
  const back = () => {
    if (step <= 2) {
      setStep((s) => Math.max(s - 1, 0));
    } else if (step > 2) {
      // Go back from OTP steps to form
      setStep(2);
    }
  };

  // 🔢 6-box OTP helpers — always keep `otp` a compact left-to-right digit string
  const focusOtp = (i) => {
    const t = Math.max(0, Math.min(i, 5));
    otpRefs.current[t]?.focus();
  };
  const handleOtpChange = (index, value) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return;
    const nextOtp = (otp + digits).slice(0, 6);
    setOtp(nextOtp);
    focusOtp(nextOtp.length);
  };
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const nextOtp = otp.slice(0, -1);
      setOtp(nextOtp);
      focusOtp(nextOtp.length);
    } else if (e.key === 'ArrowLeft') {
      focusOtp(index - 1);
    } else if (e.key === 'ArrowRight') {
      focusOtp(index + 1);
    }
  };
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const digits = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (digits) {
      setOtp(digits);
      focusOtp(digits.length);
    }
  };

  // 🔧 NEW: Send OTP for seller signup
  const handleSendOTP = async () => {
    const logPrefix = '🔵 [SELLER-REGISTER-SEND-OTP]';
    console.log(`${logPrefix} ========================================`);
    console.log(`${logPrefix} START: Sending signup OTP`);

    // Validation
    if (!formData.address.trim()) {
      toast.error("Please select a shop address from suggestions");
      return;
    }

    if (!formData.firstName || !formData.email || !formData.mobile || !formData.password) {
      toast.error("Please complete all required fields");
      return;
    }

    try {
      setOtpLoading(true);

      console.log(`${logPrefix} 📤 Calling sendSignupOTP service...`);
      console.log(`${logPrefix} Input:`, {
        firstName: formData.firstName?.substring(0, 10) + '...',
        email: formData.email,
        mobileNumber: `${formData.mobile?.substring(0, 6)}****${formData.mobile?.slice(-2)}`
      });

      const response = await sendSignupOTP(
        formData.firstName.trim(),
        formData.email.trim(),
        formData.mobile.trim()
      );

      console.log(`${logPrefix} 📥 Service Response:`, {
        success: response.success,
        maskedPhone: response.data?.phoneNumber
      });

      if (response.success) {
        setMaskedPhone(response.data?.phoneNumber || '');
        setStep(3); // Move to OTP verification step
        trackSignupStep('seller', 'otp_sent');
        toast.success(response.message || 'OTP sent to your phone number');
        console.log(`${logPrefix} ✅ OTP sent, moving to step 3 (OTP Verification)`);
      } else {
        toast.error(response.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error(`${logPrefix} ❌ ERROR:`, error);
      toast.error(error.message || error.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setOtpLoading(false);
      console.log(`${logPrefix} ========================================`);
    }
  };

  // 🔧 NEW: Verify OTP and register seller
  const handleVerifyOTPAndRegister = async (e) => {
    e.preventDefault();

    const logPrefix = '🟢 [SELLER-REGISTER-VERIFY-OTP]';
    console.log(`${logPrefix} ========================================`);
    console.log(`${logPrefix} START: Verifying OTP and registering seller`);

    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      setOtpLoading(true);

      const shop = {
        name: formData.shopName.trim(),
        address: formData.address.trim(),
        phoneNumber: {
          main: formData.shopPhone.trim()
        },
        gstNumber: formData.gst.trim(),
        category: formData.category,
        location: shopCoordinates ? {
          type: "Point",
          coordinates: shopCoordinates
        } : {
          type: "Point",
          coordinates: [0, 0]
        }
      };

      const bankDetails = {
        accountNumber: formData.upi.trim()
      };

      console.log(`${logPrefix} 📤 Calling verifySignupOTPAndRegister service...`);
      console.log(`${logPrefix} OTP: ${otp.substring(0, 2)}****`);

      const response = await verifySignupOTPAndRegister(
        formData.firstName.trim(),
        formData.email.trim(),
        formData.password,
        formData.mobile.trim(),
        otp,
        shop,
        bankDetails
      );

      console.log(`${logPrefix} 📥 Service Response:`, {
        success: response.success,
        hasToken: !!response.data?.token,
        sellerId: response.data?._id
      });

      if (response.success && response.data && response.data.token) {
        trackSignup('seller', response.data._id);
        try {
          console.log('🔄 Auto-login after seller registration...');
          contextLoginSeller(response.data);
          toast.success(`Welcome to Zammer, ${response.data.firstName}!`);
          navigate("/seller/dashboard");
        } catch (loginError) {
          console.error('Seller auto-login failed:', loginError);
          toast.success("Seller registered successfully! Please login.");
          navigate("/seller/login");
        }
      } else {
        toast.success("Seller registered successfully! Please login.");
        navigate("/seller/login");
      }
    } catch (error) {
      console.error(`${logPrefix} ❌ ERROR:`, error);
      toast.error(
        error.message ||
        error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.msg ||
        "Registration failed"
      );
    } finally {
      setOtpLoading(false);
      console.log(`${logPrefix} ========================================`);
    }
  };

  /* ---------- STEP UI ---------- */
  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <>
            <h3 className="text-xl font-bold text-gray-800 mb-5">Personal Details</h3>

            <div className="space-y-4">
              <div>
                <label className={labelClasses}>First Name</label>
                <input
                  className={inputClasses}
                  name="firstName"
                  placeholder="Your name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label className={labelClasses}>Email Address</label>
                <input
                  className={inputClasses}
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label className={labelClasses}>Password</label>
                <div className="relative">
                  <input
                    className={inputClasses + " pr-12"}
                    type={showPassword ? "text" : "password"}
                    name="password"
                    minLength={6}
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-orange-500 transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                <p className={hintClasses}>Minimum 6 characters</p>
              </div>

              <div>
                <label className={labelClasses}>Mobile Number</label>
                <input
                  className={inputClasses}
                  name="mobile"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                />
                <p className={hintClasses}>We'll send an OTP to verify this number</p>
              </div>
            </div>
          </>
        );

      case 1:
        return (
          <>
            <h3 className="text-xl font-bold text-gray-800 mb-5">Shop Details</h3>

            <div className="space-y-4">
              <div>
                <label className={labelClasses}>Shop / Brand Name</label>
                <input
                  className={inputClasses}
                  name="shopName"
                  placeholder="e.g. Grootinn Fashion"
                  value={formData.shopName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label className={labelClasses}>Shop Phone Number</label>
                <input
                  className={inputClasses}
                  name="shopPhone"
                  type="tel"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  placeholder="10-digit shop phone"
                  value={formData.shopPhone}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* 🎯 Google Places address with premium look */}
              <div>
                <label className={labelClasses}>Shop Address</label>
                <GooglePlacesAutocomplete
                  className={inputClasses}
                  placeholder="Search your shop address"
                  value={formData.address}
                  onChange={(val) =>
                    setFormData((f) => ({ ...f, address: val }))
                  }
                  // 🎯 Extract coordinates when place is selected
                  onPlaceSelected={(placeData) => {
                    console.log('🗺️ Place selected:', placeData);
                    if (placeData && placeData.coordinates) {
                      setShopCoordinates(placeData.coordinates);
                      console.log('📍 Coordinates extracted:', placeData.coordinates);
                    }
                  }}
                />
              </div>

              <div>
                <label className={labelClasses}>GST Number <span className="font-normal text-gray-400">(optional)</span></label>
                <input
                  className={inputClasses}
                  name="gst"
                  placeholder="GST number"
                  value={formData.gst}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className={labelClasses}>Category</label>
                <select
                  className={inputClasses + " appearance-none cursor-pointer"}
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        );

      case 2:
        return (
          <>
            <h3 className="text-xl font-bold text-gray-800 mb-5">Payment Details</h3>

            <div className="space-y-4">
              <div>
                <label className={labelClasses}>UPI ID / Bank Account Number</label>
                <input
                  className={inputClasses}
                  name="upi"
                  placeholder="UPI ID or bank account number"
                  value={formData.upi}
                  onChange={handleChange}
                  required
                />
                <p className={hintClasses}>Where you'll receive your payouts</p>
              </div>
            </div>
          </>
        );

      case 3:
        // 🔧 OTP Verification Step
        return (
          <>
            <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">Verify OTP</h3>
            <p className="text-gray-500 text-center mb-6">
              Enter the 6-digit code sent to{" "}
              <span className="font-semibold text-gray-700">{maskedPhone || formData.mobile}</span>
            </p>

            <form onSubmit={handleVerifyOTPAndRegister}>
              <div className="flex justify-center gap-2 sm:gap-3 mb-6" onPaste={handleOtpPaste}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={otp[i] || ''}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className="w-11 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold bg-white/70 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              <button
                type="submit"
                className={primaryBtnClasses}
                disabled={otpLoading || !otp || otp.length !== 6}
              >
                {otpLoading && (
                  <span className="w-5 h-5 mr-2 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block"></span>
                )}
                {otpLoading ? "Verifying..." : "Verify OTP & Register"}
              </button>
            </form>

            <button
              type="button"
              className={secondaryBtnClasses + " mt-3"}
              onClick={() => {
                setStep(2);
                setOtp('');
              }}
              disabled={otpLoading}
            >
              Back to Payment Details
            </button>
          </>
        );

      default:
        return null;
    }
  };

  /* ---------- RENDER ---------- */
  return (
    <div className="relative overflow-hidden min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30 flex items-center justify-center p-4 py-10">
      {/* Scoped styling for the Google Places dropdown (injected into body) */}
      <style>
        {`
          .pac-container {
            border-radius: 12px !important;
            margin-top: 6px !important;
            border: 1px solid #f3f4f6 !important;
            box-shadow: 0 12px 30px rgba(0,0,0,0.12) !important;
            font-family: inherit !important;
            overflow: hidden !important;
          }
          .pac-item {
            padding: 10px 14px !important;
            cursor: pointer !important;
            border-top: 1px solid #f9fafb !important;
            color: #374151 !important;
          }
          .pac-item:hover { background-color: #fff7ed !important; }
          .pac-item-selected, .pac-item:first-child { background-color: #fff7ed !important; }
          .pac-matched { color: #ea580c !important; font-weight: 600 !important; }
        `}
      </style>

      {/* Decorative background blobs */}
      <div className="pointer-events-none absolute top-[-6rem] right-[-6rem] w-72 h-72 bg-orange-300/30 rounded-full blur-3xl"></div>
      <div className="pointer-events-none absolute bottom-[-6rem] left-[-6rem] w-72 h-72 bg-amber-300/20 rounded-full blur-3xl"></div>

      <div className="relative w-full max-w-lg animate-fade-in-up">
        {/* Gradient glow behind the glass card */}
        <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 rounded-3xl blur opacity-20"></div>

        <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/50 p-6 sm:p-8">
          {/* Logo + heading */}
          <div className="flex flex-col items-center mb-6">
            <img
              src="/images/zammer-official-logo.png"
              alt="ZAMMER"
              className="h-16 w-auto object-contain mb-4"
            />
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-500 via-orange-600 to-orange-700 bg-clip-text text-transparent text-center">
              Create Seller Account
            </h1>
            <p className="text-gray-500 text-center mt-2">Join ZAMMER and start selling</p>
          </div>

          {/* Stepper */}
          <div className="flex items-center justify-center mb-8">
            {steps.map((label, i) => {
              const isDone = step > i;
              const isActive = step === i;
              return (
                <React.Fragment key={label}>
                  <div className="flex flex-col items-center">
                    <div
                      className={
                        "w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 " +
                        (isDone
                          ? "bg-green-500 text-white shadow-lg"
                          : isActive
                          ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white scale-110 shadow-lg"
                          : "bg-gray-100 text-gray-400 border border-gray-200")
                      }
                    >
                      {isDone ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span
                      className={
                        "mt-1.5 text-[11px] font-medium transition-colors " +
                        (isActive ? "text-orange-600" : isDone ? "text-green-600" : "text-gray-400")
                      }
                    >
                      {label}
                    </span>
                  </div>
                  {i < steps.length - 1 && (
                    <div
                      className={
                        "flex-1 h-0.5 mx-1 sm:mx-2 mb-5 rounded-full transition-colors duration-300 " +
                        (step > i ? "bg-green-500" : "bg-gray-200")
                      }
                    ></div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Step content (re-animates on step change) */}
          <div key={step} className="animate-fade-in-up">
            {renderStep()}

            {/* Navigation buttons for form steps 0-2 (OTP step has its own buttons) */}
            {step <= 2 && (
              <div className="mt-6 space-y-3">
                {step === 2 ? (
                  <button
                    type="button"
                    className={primaryBtnClasses}
                    onClick={handleSendOTP}
                    disabled={loading || otpLoading}
                  >
                    {(loading || otpLoading) && (
                      <span className="w-5 h-5 mr-2 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block"></span>
                    )}
                    {loading || otpLoading ? "Sending OTP…" : "Send OTP"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={primaryBtnClasses}
                    onClick={next}
                    disabled={loading || otpLoading}
                  >
                    Continue
                  </button>
                )}

                {step > 0 && (
                  <button
                    type="button"
                    className={secondaryBtnClasses}
                    onClick={back}
                    disabled={loading || otpLoading}
                  >
                    Back
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-sm text-gray-500 mt-5">
          Already have an account?{" "}
          <Link to="/seller/login" className="text-orange-600 hover:text-orange-700 font-semibold transition-colors">
            Sign in here
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SellerRegister;
